import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { IconButton, Button } from "@mui/material";
import { ModeEditOutlineTwoTone, InfoOutlined, Add } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import CustomTabs from "components/Shared/CustomTabs";
import TableFilter from "components/Shared/AppTable/TableFilter";
import ParamsUserAutocomplete from "components/Shared/ParamsUserAutocomplete";
import HighlightedText from "components/Shared/HighlightedText";
import ParamsSortByTimestamp from "components/Shared/ParamsSortByTimestamp";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

import { useLazyListListQuery } from "store/services/listService";
import { useLazyGetTaskListQuery } from "store/services/taskApi";

import {
  useAuth,
  useAppMeta,
  useTablePagination,
  useGetUrlParams,
} from "hooks";

import { ENTITY_LIST_CONSTANTS } from "data/static";

const DUE_LIST = [
  { label: "Current Task", id: "false" },
  { label: "Outstanding Task", id: "true" },
];

const TASK_LIST_SORT_OPTIONS = [
  {
    label: "Created At (Desc)",
    sortBy: "createdAt",
    direction: -1,
  },
  {
    label: "Created At (Asc)",
    sortBy: "createdAt",
    direction: 1,
  },
  {
    label: "Due Date (Desc)",
    sortBy: "dueDate",
    direction: -1,
  },
  {
    label: "Due Date (Asc)",
    sortBy: "dueDate",
    direction: 1,
  },
];

const TaskListScreen = () => {
  const { isEntityAdmin } = useAuth();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const URL_PARAMS = useGetUrlParams();

  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Open";
  const keyword = searchParams.get("search") || "";
  const responsible = searchParams.get("responsible") || null;
  const sortBy = searchParams.get("sortBy") || "createdAt";
  const sortDirection = searchParams.get("sortDirection") || -1;
  const isDue = searchParams.get("isDue") || null;
  const isStage = searchParams.get("stage") || null;

  const breadcrumbs = [{ title: "Tasks" }];

  // State to store badge counts for tabs
  const [tabCounts, setTabCounts] = useState({
    Open: 0,
    "In Progress": 0,
    Verification: 0,
    Discarded: 0,
    Closed: 0,
    All: 0,
    Inactive: 0,
  });

  const taskListTabs = useMemo(
    () => [
      { label: "Open", value: "Open", badgeCount: tabCounts.Open },
      {
        label: "In Progress",
        value: "In Progress",
        badgeCount: tabCounts["In Progress"],
      },
      {
        label: "Verification",
        value: "Verification",
        badgeCount: tabCounts.Verification,
      },
      {
        label: "Discarded",
        value: "Discarded",
        badgeCount: tabCounts.Discarded,
      },
      { label: "Closed", value: "Closed" },
      { label: "All", value: "All" },
      { label: "Inactive", value: "Inactive" },
    ],
    [tabCounts]
  );

  const selectedDue = useMemo(
    () => DUE_LIST?.find((due) => due?.id === isDue) ?? "",
    [isDue]
  );

  const columns = [
    {
      name: "Title",
      selector: (row) => row?.title,
      cell: (row) => (
        <Link
          to={`/app/crm/task/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-500 hover:underline"
        >
          {row?.title ? (
            <HighlightedText text={row?.title} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    ...(!isEntityAdmin
      ? [
        {
          name: "Entity",
          cell: (row) => (
            <div
              title={row?.entity?.displayName ?? row?.entity?.name}
              className="line-clamp-2"
            >
              {row?.entity?.displayName ?? row?.entity?.name}
            </div>
          ),
        },
      ]
      : []),
    {
      name: "Responsible",
      selector: (row) => row?.responsible?.name ?? "N/A",
    },
    {
      name: "Priority",
      selector: (row) => row.priority,
      align: "center",
    },
    {
      name: "Stage",
      selector: (row) => row?.stage?.name ?? "N/A",
      cell: (row) => row?.stage?.name ?? "N/A",
    },
    {
      name: "Due Date",
      selector: (row) => row?.dueDate,
      cell: (row) => <AppTableDateTime date={row?.dueDate} dateOnly />,
    },
    {
      name: "Created By",
      selector: (row) => row?.createdBy?.name ?? "N/A",
      cell: (row) => row?.createdBy?.name ?? "N/A",
    },
    {
      name: "Created At",
      selector: (row) => (
        <AppTableDateTime date={row?.createdAt} dateOnly />
      ) ?? "N/A",
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/task/${row._id}/detail`}>
            <IconButton color="primary" aria-label="View Task">
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/task/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit Task">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
      maxWidth: "100px",
    },
  ];

  // RTK Query
  const [
    listTasks,
    { data: taskRes, isLoading: loadingTask, isFetching: fetchingTask, error },
  ] = useLazyGetTaskListQuery();

  // Separate instances for tab counts
  const [fetchOpenCount] = useLazyGetTaskListQuery();
  const [fetchInProgressCount] = useLazyGetTaskListQuery();
  const [
    fetchTaskStages,
    {
      data: taskStages = [],
      isLoading: loadingTaskStages,
      isFetching: fetchingTaskStages,
    },
  ] = useLazyListListQuery();

  const isBusy =
    loadingTask || fetchingTask || loadingTaskStages || fetchingTaskStages;

  const _noTaskStages = !isBusy && !taskStages?.length;

  const _redirectProps = _noTaskStages && {
    message: `${ENTITY_LIST_CONSTANTS.TASK_STAGES} List not initialized! Please setup the list to track the task stages.`,
    link: `/app/crm/entity-list/detail/${ENTITY_LIST_CONSTANTS.TASK_STAGES}?type=${ENTITY_LIST_CONSTANTS.TASK_STAGES}`,
  };

  // Fetch main task data
  useEffect(() => {
    const stageGroupMap = {
      Open: "Open",
      "In Progress": "In Progress",
      Verification: "Verification",
      Discarded: "Discarded",
      Closed: "Closed",
      All: "Open,In Progress,Verification,Closed,Discarded",
      Inactive: null,
    };

    listTasks({
      active: activeTab === "Inactive" ? "false" : "true",
      stageGroup: isStage || stageGroupMap[activeTab] || null,
      page: currentPage,
      limit: perPage,
      sortBy,
      sortDirection: Number(sortDirection),
      ...(keyword && { keyword }),
      ...(responsible && { responsible }),
      ...(isDue && { isDue }),
    });
  }, [
    activeTab,
    currentPage,
    isDue,
    isStage,
    keyword,
    listTasks,
    perPage,
    responsible,
    sortBy,
    sortDirection,
  ]);

  // Fetch counts for all tabs
  useEffect(() => {
    const fetchTabCounts = async () => {
      const baseParams = {
        page: 1,
        limit: 1,
        ...(keyword && { keyword }),
        ...(responsible && { responsible }),
        ...(isDue && { isDue }),
      };

      const [openResponse, inProgressResponse,] = await Promise.all([
        fetchOpenCount({
          ...baseParams,
          active: true,
          stageGroup: "Open",
        }).unwrap(),
        fetchInProgressCount({
          ...baseParams,
          active: true,
          stageGroup: "In Progress",
        }).unwrap(),
      ]);
      setTabCounts({
        Open: openResponse?.totalItems || 0,
        "In Progress": inProgressResponse?.totalItems || 0,
      });
    };
    fetchTabCounts();
  }, [fetchInProgressCount, fetchOpenCount, isDue, keyword, responsible]);

  useEffect(() => {
    fetchTaskStages({
      type: ENTITY_LIST_CONSTANTS.TASK_STAGES,
      isSubType: false,
      includeChildren: true,
      status: "Active",
    });
  }, [fetchTaskStages]);

  useAppMeta("Tasks");
  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      isBusy={isBusy}
      headerActions={!_noTaskStages && <PageActions />}
    >
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Redirect {..._redirectProps} />
      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={taskListTabs} activeTab={activeTab} />
        <TableFilter
          filterMineKey="Responsible"
          searchLabel="Title"
          disabled={isBusy}
        >
          <ParamsUserAutocomplete label="Responsible" disabled={isBusy} />
          <ParamsSortByTimestamp
            sortOptions={TASK_LIST_SORT_OPTIONS}
            disabled={isBusy}
          />
          {activeTab !== "Closed" && (
            <BaseAutocomplete
              className="lg:ml-auto xl:w-[190px]"
              label="Unresolved"
              options={DUE_LIST}
              value={
                selectedDue && {
                  label: selectedDue?.label,
                  id: selectedDue?.id,
                }
              }
              onChange={(_, issue) => {
                if (!issue) {
                  delete URL_PARAMS.isDue;
                  delete URL_PARAMS.stage;
                  return setSearchParams(URL_PARAMS);
                }
                setSearchParams({
                  ...URL_PARAMS,
                  isDue: issue?.id,
                  stage:
                    activeTab === "All"
                      ? "Open,In Progress,Verification"
                      : activeTab === "Open"
                        ? "Open,In Progress,Verification"
                        : activeTab === "Inactive"
                          ? null
                          : activeTab,
                });
              }}
              disabled={isBusy}
            />
          )}
        </TableFilter>
        <AppTable
          columns={columns}
          data={taskRes?.data ?? []}
          progressPending={isBusy}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={taskRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

const PageActions = () => {
  return (
    <Link to="/app/crm/task/create">
      <Button startIcon={<Add />} variant="contained">
        New Task
      </Button>
    </Link>
  );
};

export default TaskListScreen;